/**
 * Screen-local fetch module for the Users screen (epic 103 D3/D8, task 03) —
 * the partner-credentials precedent: `lib/api.ts` is never edited, so this
 * screen owns its endpoints. The envelope/error handling mirrors
 * `lib/api.ts`'s private `request()` line for line. Wire types are api-local
 * (`users-schemas.ts` is deliberately not shared), so the row/response unions
 * are mirrored here next to the calls that speak them.
 */

import { apiFetch } from '../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/** The two roles on `admin_user` (epic 103 D1) — mirrors the api wire union. */
export type AdminUserRole = 'admin' | 'employee';

/**
 * One managed user row on the wire (epic 103 D3 list shape): bigint PK →
 * string, dates → ISO strings.
 */
export interface UserRow {
  id: string;
  email: string;
  displayName: string;
  role: AdminUserRole;
  isActive: boolean;
  mustChangePassword: boolean;
  failedAttempts: number;
  lockedUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Create/reset result: the row, plus the generated password the ONE time it exists. */
export interface UserRowWithGeneratedPassword {
  user: UserRow;
  generatedPassword: string | null;
}

/**
 * Error twin of `lib/api.ts`'s `ApiError` — same `{ status, message,
 * validationErrors }` shape, so this screen catches errors exactly as it
 * catches the shared client's. Re-thrown from here for callers that already
 * import the shared one.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Map a backend validation error array (`{path, message, code}`) into a
 * `Record<path, message[]>`. The root path is normalized to `_form`.
 * Verbatim from `lib/api.ts`.
 */
function mapValidationErrors(errors: unknown): Record<string, string[]> | undefined {
  if (!Array.isArray(errors)) return undefined;
  const out: Record<string, string[]> = {};
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { path?: string; message?: string };
    const key = e.path && e.path.length > 0 ? e.path : '_form';
    const msg = e.message ?? 'Invalid';
    if (!out[key]) out[key] = [];
    out[key].push(msg);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** API response envelope — matches the backend's ApiResponse<T> shape. */
interface ApiResponse<T> {
  data: T;
}

/** The wire row for create/reset — `generatedPassword` present only when generated. */
type UserWithPasswordWire = UserRow & { generatedPassword?: string };

/** Split the wire row into the screen's result: the row + a nullable secret. */
function toResult(wire: UserWithPasswordWire): UserRowWithGeneratedPassword {
  const { generatedPassword, ...user } = wire;
  return { user, generatedPassword: generatedPassword ?? null };
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(`${API_BASE_URL}${ADMIN_PREFIX}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      typeof errorData.message === 'string' && errorData.message.length > 0
        ? errorData.message
        : response.statusText;
    throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
  }

  const text = await response.text();
  if (text.length === 0) {
    throw new ApiError(0, 'Empty response from the API');
  }
  const envelope: ApiResponse<T> = JSON.parse(text);
  return envelope.data;
}

/** Every managed user, newest-first (the API's default order). */
export function getUsers(): Promise<UserRow[]> {
  return request<UserRow[]>('/users');
}

/** POST /admin/users — `password` absent → the server generates one and returns it once. */
export function createUser(body: {
  email: string;
  displayName: string;
  role: AdminUserRole;
  password?: string;
}): Promise<UserRowWithGeneratedPassword> {
  return request<UserWithPasswordWire>('/users', {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(toResult);
}

/** PATCH /admin/users/:id — self role/isActive change → 400 (the api's self-guard). */
export function updateUser(
  id: string,
  body: { displayName?: string; role?: AdminUserRole; isActive?: boolean }
): Promise<UserRow> {
  return request<UserRow>(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** POST /admin/users/:id/reset-password — absent newPassword → generated + returned once. */
export function resetUserPassword(
  id: string,
  body: { newPassword?: string }
): Promise<UserRowWithGeneratedPassword> {
  return request<UserWithPasswordWire>(`/users/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(toResult);
}
